import { useState, useEffect, useRef } from 'react';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { searchEasykitTasks } from '@/api/easykit';
import type { EasykitSearchResult } from '@/api/easykit';

function TypeBadge({ name }: { name: string }) {
  const isDT = name.includes('DT');
  const isLev = name.includes('m²') || name.toLowerCase().includes('m2');
  if (isDT) return <span className="text-[9px] font-bold text-red-600 bg-red-100 px-1 rounded">DT</span>;
  if (isLev) return <span className="text-[9px] font-bold text-blue-600 bg-blue-100 px-1 rounded">LEV</span>;
  return <span className="text-[9px] font-bold text-gray-500 bg-gray-100 px-1 rounded">&mdash;</span>;
}

/**
 * Search the EasyKit task pool and either:
 *   - enter schedule-mode on a not-yet-planned result (user picks a cell), or
 *   - navigate to the week containing a planned result and select it.
 * Debounced (300 ms), closes on click-outside.
 */
export function EasykitTaskSearch() {
  const enterScheduleMode = useEasykitPlanningStore((s) => s.enterScheduleMode);
  const selectTask = useEasykitPlanningStore((s) => s.selectTask);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EasykitSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounced search.
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchEasykitTasks(searchQuery);
        setSearchResults(results);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close on click outside.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleResultClick(result: EasykitSearchResult) {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);

    if (!result.isPlanned) {
      enterScheduleMode(result.id, result.name, result.projectName, result.orderNumber, result.address);
      return;
    }
    if (!result.dateDeadline) return;

    // Navigate to the week containing the task, then select it after refetch.
    const weekDiff = computeWeekDiff(result.dateDeadline.slice(0, 10));
    const store = useEasykitPlanningStore.getState();
    useEasykitPlanningStore.setState({ weekOffset: weekDiff });
    store.fetchPlanning(weekDiff)
      .then(() => selectTask(result.id))
      .catch(() => { /* navigation failed, stay on current week */ });
  }

  return (
    <div ref={searchRef} className="relative hidden lg:block">
      <label htmlFor="ek-search-input" className="sr-only">Zoek taak</label>
      <input
        id="ek-search-input"
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Zoek taak om in te plannen..."
        className="w-64 lg:w-96 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue/30 focus:border-td-blue"
      />
      {searchLoading && <span className="absolute right-3 top-2 text-xs text-gray-400">...</span>}

      {searchOpen && searchResults.length > 0 && (
        <div className="absolute top-full left-0 mt-1 w-96 bg-white border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {searchResults.map((result) => (
            <button
              key={result.id}
              type="button"
              onClick={() => handleResultClick(result)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center gap-2">
                <TypeBadge name={result.name} />
                <span className="text-xs font-bold text-td-navy truncate">{result.name}</span>
              </div>
              {result.address && <p className="text-[10px] text-td-light truncate mt-0.5">{result.address}</p>}
              <div className="flex items-center gap-1 mt-0.5">
                {result.projectName && <span className="text-[10px] text-td-muted truncate">{result.projectName}</span>}
                {result.orderNumber && <span className="text-[10px] text-td-light">&middot; {result.orderNumber}</span>}
                {result.isPlanned && result.dateDeadline && (
                  <span className="text-[10px] text-green-600 font-medium ml-auto">
                    &#10003; {result.dateDeadline.slice(8, 10)}/{result.dateDeadline.slice(5, 7)}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {searchOpen && searchResults.length === 0 && searchQuery.length >= 3 && !searchLoading && (
        <div className="absolute top-full left-0 mt-1 w-96 bg-white border rounded-lg shadow-lg z-50 p-3 text-xs text-gray-500">
          Geen taken gevonden
        </div>
      )}
    </div>
  );
}

/** Whole-week offset between a YYYY-MM-DD task date and today. */
function computeWeekDiff(taskDateStr: string): number {
  const taskDate = new Date(taskDateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDay = today.getDay() || 7;
  const todayMonday = new Date(today);
  todayMonday.setDate(today.getDate() - (todayDay - 1));
  const taskDay = taskDate.getDay() || 7;
  const taskMonday = new Date(taskDate);
  taskMonday.setDate(taskDate.getDate() - (taskDay - 1));
  return Math.round((taskMonday.getTime() - todayMonday.getTime()) / (7 * 24 * 60 * 60 * 1000));
}
